import { T, SERIES, ils0, num, tnum, pct2 } from '../../../../theme/tokens';
import type { PayerRow } from '../types';

/* Revenue split by payer. Thick ring, a hairline of card-colour between slices, the
   percentage in grey just OUTSIDE each slice, and the total in the middle.
   The percentages used to sit white on the ring; "74.97%" is wider than the ring
   is thick, so it spilled over the edge and "15.82%" lost its first digit against
   the hole (Ohad, 02/11: "לא רואים את המספרים בעוגה"). Six payers at most — above
   that a ring stops being readable and this should become a bar list. */
export function Donut({ rows, total }: { rows: PayerRow[]; total: number }) {
  const S = 264, cx = S / 2, cy = S / 2, R = 92, r = 60, LR = R + 16;

  /* A payer with no tariff has revenue 0. Drawn, it is a zero-width wedge and a
     legend row reading "₪0 (0%)" — as if the clinic had worked for nothing. The
     work is real, so those payers are named under the chart with their meeting
     count instead of being given a slice they did not earn. */
  const priced = rows.filter((p) => !p.unpriced && p.revenue > 0);
  const unpriced = rows.filter((p) => p.unpriced && p.meetings > 0);

  let acc = 0;
  const arcs = priced.map((p, i) => {
    const frac = total ? p.revenue / total : 0;
    const a0 = acc * 2 * Math.PI - Math.PI / 2;
    acc += frac;
    const a1 = acc * 2 * Math.PI - Math.PI / 2;
    const big = frac > 0.5 ? 1 : 0;
    const pt = (ang: number, rad: number) => `${cx + rad * Math.cos(ang)} ${cy + rad * Math.sin(ang)}`;
    const mid = (a0 + a1) / 2;
    return {
      d: `M ${pt(a0, R)} A ${R} ${R} 0 ${big} 1 ${pt(a1, R)} L ${pt(a1, r)} A ${r} ${r} 0 ${big} 0 ${pt(a0, r)} Z`,
      color: SERIES[i % SERIES.length],
      frac, payer: p.payer, revenue: p.revenue,
      lx: cx + LR * Math.cos(mid),
      ly: cy + LR * Math.sin(mid),
    };
  });

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap' }}>
      <svg
        width={S} height={S} viewBox={`0 0 ${S} ${S}`} role="img"
        style={{ flexShrink: 0 }}
        aria-label={`הכנסות לפי קופה: ${arcs.map((a) => `${a.payer} ${pct2(a.frac * 100)}`).join(', ')}`}
      >
        {arcs.map((a) => (
          <g key={a.payer}>
            <path d={a.d} fill={a.color} stroke={T.card} strokeWidth={2.5}>
              <title>{`${a.payer} · ${ils0(a.revenue)}`}</title>
            </path>
            {/* Outside the ring, grey, and only for a slice with an arc long enough
                that two neighbours' labels cannot meet. The percentage is in the
                legend for every payer, so a slice without one loses nothing. */}
            {a.frac > 0.08 && (
              <text
                x={a.lx} y={a.ly} fill={T.inkSoft} fontSize={11} fontWeight={600}
                textAnchor="middle" dominantBaseline="central" style={tnum}
              >
                {`${pct2(a.frac * 100)}`}
              </text>
            )}
          </g>
        ))}
        <text x={cx} y={cy - 4} textAnchor="middle" fontSize={16} fontWeight={800} fill={T.ink} style={tnum}>
          {ils0(total)}
        </text>
        <text x={cx} y={cy + 14} textAnchor="middle" fontSize={10} fill={T.inkSoft}>
          סך ההכנסות
        </text>
      </svg>

      {/* A legend is always present, and each wide slice is direct-labelled as well:
          identity never rests on colour alone. */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 9, minWidth: 148, flex: 1 }}>
        {arcs.map((a) => (
          <div key={a.payer} style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
            <span aria-hidden style={{
              width: 9, height: 9, borderRadius: 3, background: a.color, flexShrink: 0,
            }} />
            <span style={{ fontSize: 12.5, fontWeight: 700, color: T.ink, flex: 1, minWidth: 0 }}>
              {a.payer}
            </span>
            <span style={{ ...tnum, fontSize: 12, color: T.inkSoft, whiteSpace: 'nowrap' }}>
              {ils0(a.revenue)}
              <span style={{ color: T.inkFaint }}>{` · ${pct2(a.frac * 100)}`}</span>
            </span>
          </div>
        ))}

        {unpriced.length > 0 && (
          <div style={{ borderTop: `1px solid ${T.borderSoft}`, paddingTop: 8, marginTop: 2 }}>
            <div style={{ fontSize: 10.5, fontWeight: 700, color: T.inkFaint, marginBottom: 4 }}>
              ללא תעריף
            </div>
            {unpriced.map((p) => (
              <div
                key={p.payer}
                style={{ ...tnum, fontSize: 11.5, color: T.inkSoft }}
                title="שולם בכרטיס אשראי שאינו קשור לפגישה — העבודה נספרת, ההכנסה לא ניתנת לשיוך"
              >
                {p.payer} · {num(p.meetings)} פגישות
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
